import { LootboxSnakeCard } from 'components/cards/lootboxSnakeCard';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useStoreContext } from 'store/app';
import { useLocation, useNavigate } from 'react-router';

import './openLootboxScreen.styl';
import { ISnake, LootboxTypes } from 'src/types';
import { routes } from 'utils/router';
import { getRandomInt } from 'utils/getRandomInt';
import classNames from 'classnames';
import { OverlayStarAnimation } from 'components/layout/overlayStarAnimation';
import Label from 'components/core/Label';
import { useTranslationsContext } from 'store/translations';

export const OpenLootboxScreen = observer(() => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const {
        userState,
        catalogConfig,
        openLootbox,
        selectSnake,
        isCollectionFinished,
        isMainPartFinished,
        isAnonym,
        getCollectionBySnakeId,
        logOperation,
        isMob,
        openLink,
        settings,
        linkToRules
    } = useStoreContext();
    const { translate } = useTranslationsContext();
    const [selectedSnake, setSelectedSnake] = useState<ISnake>();

    const { lootboxToOpen } = (state || {}) as { lootboxToOpen?: number };

    const boxObj = userState?.unopened_bonus_lootboxes?.find((x) => x.id === lootboxToOpen);

    const [snake1, snake2] = useMemo(() => {
        if (isAnonym) {
            if (!catalogConfig?.snakes) {
                return [];
            }
            const sn1 = catalogConfig.snakes[getRandomInt(0, catalogConfig?.snakes.length - 1)]!;
            const collectionSn1 = getCollectionBySnakeId(sn1?.id);
            let sn2 = catalogConfig.snakes[getRandomInt(0, catalogConfig?.snakes.length - 1)]!;
            while (collectionSn1 === getCollectionBySnakeId(sn2?.id)) {
                sn2 = catalogConfig.snakes[getRandomInt(0, catalogConfig?.snakes.length - 1)]!;
            }

            return [sn1, sn2];
        }
        return (userState?.current_lutbox_snakes || []).map((snakeId) =>
            catalogConfig?.snakes.find((snake) => snake.id === snakeId)
        );
    }, [userState, isAnonym]);

    const hasAnySnakes = useMemo(() => {
        return (
            userState?.free_snakes?.length ||
            userState?.collections?.some((collection) => collection.snakes && collection.snakes.length > 0)
        );
    }, [userState]);

    useEffect(() => {
        !isAnonym && snake1 && snake2 && logOperation('snake_choice', 'render', snake1.id + ',' + snake2.id);
    }, [snake1, snake2, isAnonym]);

    useEffect(() => {
        if (isCollectionFinished || isMainPartFinished) {
            navigate(routes.main);
            return;
        }

        if (userState && !userState.current_lutbox_snakes) {
            openLootbox(lootboxToOpen);
        }
    }, []);

    useEffect(() => {
        if (selectedSnake) {
            const relatedCollection = userState?.collections?.find((x) => x.snakes.includes(selectedSnake.id));
            const isFirstDuplicate = userState?.free_snakes?.length === 1;
            if (
                relatedCollection &&
                (isFirstDuplicate || (relatedCollection.snakes.length === 3 && !relatedCollection.rolled))
            ) {
                navigate(routes.main, { state: { selectedSnake: selectedSnake.id } });
            } else {
                navigate(routes.collection, { state: { selectedSnake: selectedSnake.id } });
            }
        }
    }, [selectedSnake]);

    const onSelectSnake = useCallback(
        (snake: ISnake) => {
            selectSnake(snake.id).then(() => {
                setSelectedSnake(snake);
            });
        },
        [navigate]
    );

    const onRulesClick = () => {
        logOperation('onboard_selection', 'rules');
        openLink(linkToRules, true);
    };

    const titleKey = useMemo(() => {
        if (!hasAnySnakes) {
            return 'lootbox.open.title.first';
        }

        if (boxObj) {
            return boxObj?.key === LootboxTypes.DRAGON ? 'lootbox.open.title.dragon' : 'lootbox.open.title.quest';
        }

        return 'lootbox.open.title';
    }, [hasAnySnakes]);

    return (
        <div className={classNames('open-lootbox', { isWeb: !isMob })}>
            <OverlayStarAnimation className="open-lootbox-star" />
            <div className="open-lootbox-title">
                <Label isRawHtml of={titleKey} />
            </div>

            {snake1 || snake2 ? (
                <div className="open-lootbox-cards">
                    <LootboxSnakeCard
                        className="open-lootbox-card-left"
                        snake={snake1 || snake2!}
                        onSelect={onSelectSnake}
                    />
                    <LootboxSnakeCard
                        className="open-lootbox-card-right"
                        snake={snake2 || snake1!}
                        onSelect={onSelectSnake}
                    />
                </div>
            ) : null}
            {!isAnonym && !hasAnySnakes && settings.hideOnboarding ? (
                <div className="open-lootbox-rules">
                    {translate('lootbox.open.rules.part1')}
                    <span onClick={onRulesClick}>{translate('welcome.rules.part2')}</span>
                </div>
            ) : null}
        </div>
    );
});
